<template>
  <div class="chatbot-page">
    <!-- Header with gradient, without double navigation -->
    <header class="header">
      <div class="header-content">
        <h1 class="main-title">Our intelligent chatbot solutions</h1>
        <p class="subtitle">AI-enhanced chatbots: Conversational intelligence at the service of your business</p>
      </div>
    </header>

    <div class="content-container">
      <!-- Introduction -->
      <section class="card intro-card">
        <div class="card-content">
          <h2 class="section-title">AI Chatbot : Effective automated dialogue solutions in all types of professional contexts</h2>
          <div class="text-and-illustration">
            <div class="text-content">
              <p>
                In the face of increasing digitalization of customer experience, 
                our agency develops customized conversational solutions, tailored to your company's needs. 
                We design and deploy intelligent chatbots that concretely improve your online interactions. 
                Our personalized approach optimizes your customer service and internal processes with 
                efficient and scalable conversational tools, designed according to current technical standards.
              </p>
            </div>
            <div class="youtube-container">
              <iframe
                style="width: 100%; height: 100%; border: none; border-radius: 15px;"
                src="https://www.youtube.com/embed/27iyosTR_mk"
                title="Presentation of our AI Chatbot solutions"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen>
              </iframe>
            </div>
          </div>
        </div>
      </section>

      <!-- What is a chatbot - With integrated image on the right -->
      <section class="card what-is-card">
        <div class="card-content">
          <h2 class="section-title">What is a Chatbot ?</h2>
          <div class="text-and-illustration">
            <div class="text-content">
              <p>
                A chatbot is a computer program designed to simulate a conversation with a
                human user. Using artificial intelligence and natural language processing,
                our chatbots can understand requests, provide relevant answers, and assist
                users with various tasks. They offer a conversational interface accessible 24/7,
                reduce operational costs, and significantly improve the customer experience.
                Modern chatbots go far beyond simple automated responses: they learn,
                adapt, and evolve to offer increasingly natural and relevant interactions.
              </p>
            </div>
            <div class="chatbot-illustration">
              <img src="/img/chatbot-en.svg" alt="Interactive chatbot illustration" class="chatbot-image" />
            </div>
          </div>
        </div>
      </section>

      <!-- Why choose our chatbots -->
      <section class="card why-choose-card">
        <div class="card-content">
          <h2 class="section-title">Why choose us to develop your chatbots ?</h2>
          <div class="text-content">
            <p>
              Are you looking for a conversational solution that concretely improves
              your customer relationship and optimizes your internal processes ? 
              Our chatbots are developed with proven technologies and 
              precisely adapted to your business needs. You benefit from 
              high-performance tools that meet the current requirements of your sector, 
              with a measurable return on investment for your company.
            </p>
            <p><br>
              <a href="https://www.graine-digitale.fr/demo/chatbot/" target="_blank" 
                style="display: block; width: fit-content; margin: 0 auto; background-color: #7c3aed1a; color: inherit; padding: 10px; border-radius: 9999px;">
                <strong>Try our demonstration chatbot here</strong>
              </a><br>
              <img src="/img/chatbot-demo.png" alt="Chatbot Demo" style="display: block; margin: auto;" />
            </p>
          </div>

        </div>
      </section>

      <!-- Advantages (Cards) -->
      <div class="advantages-grid">
        <!-- Advantage 1 -->
        <div class="advantage-card">
          <div class="advantage-number">1</div>
          <h3 class="advantage-title">Customer experience improvement</h3>
          <p class="advantage-description">
            Your chatbots will be designed to instantly respond to your customers' needs, offer
            24/7 support, and effectively guide users throughout their journey. They will reduce
            waiting times, increase customer satisfaction, and enable personalized interactions
            at scale.
          </p>
        </div>

        <!-- Advantage 2 -->
        <div class="advantage-card">
          <div class="advantage-number">2</div>
          <h3 class="advantage-title">Internal process optimization</h3>
          <p class="advantage-description">
            By automating repetitive tasks, our solutions allow your teams to focus
            on high-value missions while reducing operational costs. Chatbots
            can handle thousands of conversations simultaneously, freeing up your human resources
            to deal with the most complex cases.
          </p>
        </div>

        <!-- Advantage 3 -->
        <div class="advantage-card">
          <div class="advantage-number">3</div>
          <h3 class="advantage-title">Customization and adaptability</h3>
          <p class="advantage-description">
            Every business is unique. That's why we develop chatbots that integrate
            perfectly with your existing systems, with an interface adaptable to your graphic charter
            and business processes. Our solutions evolve with your business and adapt to
            changes in your needs.
          </p>
        </div>

        <!-- Advantage 4 -->
        <div class="advantage-card">
          <div class="advantage-number">4</div>
          <h3 class="advantage-title">Cutting-edge technology enhanced by AI</h3>
          <p class="advantage-description">
            Thanks to artificial intelligence and machine learning, your chatbots will learn and improve
            continuously, with increasingly natural and relevant discussions. They will analyze
            past conversations to anticipate future needs and optimize the quality of responses provided.
          </p>
        </div>

        <!-- Advantage 5 -->
        <div class="advantage-card">
          <div class="advantage-number">5</div>
          <h3 class="advantage-title">Security and compliance with standards</h3>
          <p class="advantage-description">
            We place great importance on respecting security standards and data protection,
            ensuring you a reliable solution that complies with current regulations (GDPR, etc.).
            Your data and that of your customers are protected by the most advanced security protocols.
          </p>
        </div>

        <!-- Advantage 6 -->
        <div class="advantage-card">
          <div class="advantage-number">6</div>
          <h3 class="advantage-title">Detailed analyses and reports</h3>
          <p class="advantage-description">
            Your chatbots will provide valuable insights into your customers' behaviors and preferences.
            The collected data will allow you to refine your commercial strategy, identify
            opportunities for improvement, and make decisions based on concrete information.
          </p>
        </div>
      </div>


      <!-- Concrete AI use cases -->
      <section class="card use-cases-card">
              <!-- Concrete AI use cases -->

              <div class="card-content">
                <h2 class="section-title">In which areas can AI concretely benefit your business ?</h2>
                <div class="text-and-illustration">
                  <div class="text-content">
                    <p>
                      AI doesn't remain in the theoretical domain: it materializes through practical tools that improve daily life in a concrete and measurable way. 
                      See how our solutions enhanced by artificial intelligence can tangibly improve your productivity.
                    </p>
                  </div>
                  <div class="ia-enterprise-illustration">
                    <img src="/img/cas-ia-entreprise-en.svg" alt="Concrete applications of AI in business" class="ia-enterprise-image" />
                  </div>
                </div>

                
                <div class="use-cases-grid">
                  <!-- Use case 1: Customer service and communication -->
                  <div class="use-case-card">
                    <div class="use-case-icon">
                      <span class="icon-placeholder">💬</span>
                    </div>
                    <h3 class="use-case-title">Customer service and communication</h3>
                    <p class="use-case-description">
                      With our intelligent chatbots, automate the management of common questions, respond instantly to follow-up requests or frequently asked questions, reduce waiting times, and free up your staff. Our AI agent teams serve as writing assistants and also help you create professional emails and generate meeting summaries. Thus, you improve the quality and speed of communication.
                    </p>
                  </div>
                  
                  <!-- Use case 2: Process optimization -->
                  <div class="use-case-card">
                    <div class="use-case-icon">
                      <span class="icon-placeholder">⚙️</span>
                    </div>
                    <h3 class="use-case-title">Internal process optimization</h3>
                    <p class="use-case-description">
                      Automate repetitive tasks such as data entry and processing, inventory management, or order tracking. Use our AI systems that analyze sales in real-time to automatically trigger replenishments and optimize delivery routes, reducing transportation costs while improving punctuality.
                    </p>
                  </div>
                  
                  <!-- Use case 3: Content creation and marketing -->
                  <div class="use-case-card">
                    <div class="use-case-icon">
                      <span class="icon-placeholder">📈</span>
                    </div>
                    <h3 class="use-case-title">Content creation and marketing</h3>
                    <p class="use-case-description">
                      Our AI-based tools assist you in creating relevant marketing content for your communications. You benefit from optimized texts for your flyers, posters, and social media posts, respecting your visual identity. For your e-commerce activity, use our technology to analyze your customers' purchasing behaviors to offer them personalized product recommendations and improve your conversion rates.
                    </p>
                  </div>

                  <!-- Use case 4: Human resources and recruitment -->
                  <div class="use-case-card">
                    <div class="use-case-icon">
                      <span class="icon-placeholder">👥</span>
                    </div>
                    <h3 class="use-case-title">Human resources and recruitment</h3>
                    <p class="use-case-description">
                      Save time in your recruitment process with our AI systems. They allow you to sort resumes in seconds to identify profiles that best match the positions to be filled. You can also use our solution to map existing skills in your company, anticipate training needs, and thus effectively plan the development and professional evolution of your teams.
                    </p>
                  </div>

                </div>

          <div class="metrics-banner">
            <div class="metric">
              <span class="metric-value">70%</span>
              <span class="metric-label">Reduction in customer response time</span>
            </div>
            <div class="metric">
              <span class="metric-value">45%</span>
              <span class="metric-label">Decrease in operational costs</span>
            </div>
            <div class="metric">
              <span class="metric-value">25%</span>
              <span class="metric-label">Increase in conversions</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Client testimonials -->
      <section class="testimonials-section">
        <h2 class="testimonials-title">What our clients say</h2>
        
        <div class="testimonials-container">
          <!-- Testimonial 1 -->
          <div class="testimonial-card">
            <div class="testimonial-header">
              <div class="client-info">
                <h3 class="client-name">Thomas L.</h3>
                <p class="client-company">Manager of a logistics company</p>
                <div class="rating">
                  <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
                </div>
              </div>
            </div>
            <div class="testimonial-body">
              <p>Thanks to Arynae, we reduced our response time by 60% and increased
              customer satisfaction. An innovative solution that has transformed our after-sales service.</p>
            </div>
            <div class="testimonial-border"></div>
          </div>
          
          <!-- Testimonial 2 -->
          <div class="testimonial-card">
            <div class="testimonial-header">
              <div class="client-info">
                <h3 class="client-name">Claire M.</h3>
                <p class="client-company">Claims adjuster</p>
                <div class="rating">
                  <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
                </div>
              </div>
            </div>
            <div class="testimonial-body">
              <p>The chatbot designed by their team handles more than 80% of our routine requests, freeing our advisors
              for complex cases. Our ROI was achieved in less than 4 months of use.</p>
            </div>
            <div class="testimonial-border"></div>
          </div>
          
          <!-- Testimonial 3 -->
          <div class="testimonial-card">
            <div class="testimonial-header">
              <div class="client-info">
                <h3 class="client-name">Raphaël D.</h3>
                <p class="client-company">Travel agent</p>
                <div class="rating">
                  <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
                </div>
              </div>
            </div>
            <div class="testimonial-body">
              <p>The integration of our virtual assistant across multiple channels (web, WhatsApp, Messenger) has 
              allowed us to be present 24/7 for our customers, increasing our conversions by 35%.</p>
            </div>
            <div class="testimonial-border"></div>
          </div>
        </div>
        
        <div class="dots-navigation">
          <span class="dot active"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
      </section>

      <!-- Call to action -->
      <div class="cta-container">
      <span class="fake-link cta-button" @click="navigateTo('e1')">Request a demonstration</span>
    </div>
    </div>
  </div>
</template>

<script>
import { useHead } from '@vueuse/head'

export default {
  name: 'ChatbotServicePage',
  setup() {
    useHead({
      title: 'Chatbot creation - Conversational AI solutions | Arynae',
      meta: [
        { name: 'description', content: 'Development of intelligent chatbots and custom virtual assistants. Automate your customer service and improve user experience with our conversational AI solutions.' },
        { name: 'keywords', content: 'chatbot, virtual assistant, conversational AI, artificial intelligence, automated customer service, messenger bot, discord bot, whatsapp bot, Arynae' },
        { name: 'robots', content: 'index, follow' },
        { name: 'author', content: 'Arynae' },
        { 'http-equiv': 'Content-Type', content: 'text/html; charset=utf-8' },
        // Essential Open Graph
        { property: 'og:type', content: 'website' },
        { property: 'og:title', content: 'Chatbot creation - Conversational AI solutions | Arynae' },
        { property: 'og:description', content: 'Development of intelligent chatbots and custom virtual assistants. Automate your customer service and improve user experience with our conversational AI solutions.' },
        { property: 'og:url', content: 'https://arynae.com/en/chatbot' },
        { property: 'og:locale', content: 'en_US' },
        { property: 'og:image', content:'https://arynae.com/img/arynae-social-card.png' },
        { property: 'og:image:alt', content:'Digital agency - No Code solutions, SaaS, Chatbots & SEO' },
        // Basic Twitter Card
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: 'Chatbot creation - Conversational AI solutions | Arynae' },
        { name: 'twitter:description', content: 'Development of intelligent chatbots and custom virtual assistants. Automate your customer service and improve user experience with our conversational AI solutions.' },
        { name: 'twitter:image', content: 'https://arynae.com/img/arynae-social-card.png' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1.0' }
      ],
      link: [
        { rel: 'canonical', href: 'https://arynae.com/en/chatbot' },
        { rel: 'alternate', hreflang: 'fr', href: 'https://arynae.com/chatbot' },
        { rel: 'alternate', hreflang: 'en', href: 'https://arynae.com/en/chatbot' }
      ],
      htmlAttrs: {
        lang: 'en'
      }
    })
    
    return {}
  },
  data() {
    return {}
  },
  methods: {
    navigateTo(code) {
      const routes = {
        'e1': 'https://cal.com/web-passion/rdv'
      };
      
      window.open(routes[code] || code, '_blank');
    }
  }
}
</script>

<style scoped>
.chatbot-page {
  font-family: Arial, sans-serif;
  color: #333;
}

/* Header Styles */
.header {
  background: linear-gradient(to right, #2563EB, #7C3AED);
  padding-bottom: 6rem;
  color: white;
}

.header-content {
  text-align: center;
  padding: 5rem 2rem 3rem;
}

.main-title {
  font-size: 2.75rem;
  margin-bottom: 1rem;
}

.subtitle {
  font-size: 1.25rem;
  max-width: 800px;
  margin: 0 auto;
}

/* Content Container */
.content-container {
  max-width: 1200px;
  margin: -3rem auto 4rem;
  padding: 0 2rem;
}

/* Card Styles */
.card {
  background: linear-gradient(to bottom, #ffffff, #f3f4f6);
  border-radius: 0.5rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  margin-bottom: 2rem;
  overflow: hidden;
}

.card-content {
  padding: 2rem;
}

.section-title {
  font-size: 1.75rem;
  margin-bottom: 1.5rem;
  color: #333;
  display: block;
}

.text-content {
  font-size: 1rem;
  line-height: 1.6;
  color: #555;
}

/* Shared Flex Layout */
.text-and-illustration {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  align-items: center;
}

.text-and-illustration .text-content {
  flex: 2;
  min-width: 300px;
}

/* YouTube Container */
.youtube-container {
  flex: 1;
  min-width: 300px;
  height: 250px;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Chatbot Illustration */
.what-is-card .text-content {
  flex: 1.5;
  min-width: 300px;
}

.chatbot-illustration {
  flex: 1;
  min-width: 300px;
  max-width: 450px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.chatbot-image {
  max-width: 100%;
  height: auto;
  transform: scale(1.2);
}

/* Advantages Grid */
.advantages-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
}

.advantage-card {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  position: relative;
}

.advantage-number {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(to right, #2563EB, #7C3AED);
  color: white;
  font-size: 1.25rem;
  font-weight: bold;
  margin-bottom: 1rem;
}

.advantage-title {
  font-size: 1.25rem;
  margin-bottom: 1rem;
  color: #333;
}

.advantage-description {
  font-size: 0.875rem;
  line-height: 1.6;
  color: #555;
}

/* Use Cases Section */
.use-cases-card {
  margin-top: 3rem;
}

.use-cases-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin: 2.5rem 0;
}

.use-case-card {
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.08);
  padding: 1.5rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid #f0f0f0;
}

.use-case-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
}

.use-case-icon {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  background: rgba(124, 58, 237, 0.1);
  border-radius: 50%;
  padding: 1rem;
}

.use-case-icon img {
  width: 100%;
  height: auto;
}

.use-case-title {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #333;
}

.use-case-description {
  font-size: 0.95rem;
  line-height: 1.6;
  color: #555;
}

/* Metrics Banner */
.metrics-banner {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 2rem;
  background: linear-gradient(to right, rgba(37, 99, 235, 0.05), rgba(124, 58, 237, 0.05));
  border-radius: 0.75rem;
  padding: 2rem;
  margin-top: 2rem;
}

.metric {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.metric-value {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  background: linear-gradient(to right, #2563EB, #7C3AED);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
}

.metric-label {
  font-size: 1rem;
  color: #4B5563;
}

/* Testimonials Section */
.testimonials-section {
  padding: 5rem 0;
  background: #ffffff;
  margin: 5rem 0;
  position: relative;
  overflow: hidden;
}

.testimonials-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 10% 20%, rgba(37, 99, 235, 0.05) 0%, rgba(124, 58, 237, 0.05) 90%);
  z-index: 0;
}

.testimonials-title {
  font-size: 2.5rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 4rem;
  color: #1a202c;
  position: relative;
  z-index: 1;
}

.testimonials-title::after {
  content: "";
  position: absolute;
  bottom: -15px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #2563EB, #7C3AED, #EC4899);
  border-radius: 2px;
}

.testimonials-container {
  display: flex;
  gap: 2rem;
  padding: 1rem 4rem;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  overflow-x: auto;
  scrollbar-width: none;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
}

.testimonials-container::-webkit-scrollbar {
  display: none;
}

.testimonial-card {
  background: white;
  border-radius: 16px;
  min-width: 320px;
  flex: 1;
  position: relative;
  scroll-snap-align: center;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04), 0 0 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 0;
}

.testimonial-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08), 0 0 12px rgba(0, 0, 0, 0.06);
}

.testimonial-header {
  display: flex;
  align-items: center;
  padding: 1.5rem 1.5rem 0.5rem;
}

.client-info {
  flex: 1;
}

.client-name {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0 0 0.25rem;
  color: #1a202c;
}

.client-company {
  font-size: 0.9rem;
  color: #718096;
  margin: 0 0 0.5rem;
}

.rating {
  display: flex;
  font-size: 1.1rem;
  color: #FFC107;
}

.testimonial-body {
  padding: 0.5rem 1.5rem 2rem;
  font-size: 1rem;
  line-height: 1.7;
  color: #4A5568;
  flex: 1;
}

.testimonial-border {
  height: 5px;
  background: linear-gradient(90deg, #2563EB, #7C3AED, #EC4899);
  width: 100%;
}

.dots-navigation {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2.5rem;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #CBD5E0;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dot:hover {
  background-color: #A0AEC0;
}

.dot.active {
  background: linear-gradient(90deg, #2563EB, #7C3AED);
  width: 30px;
  border-radius: 10px;
}

/* Testimonials Section - Correction responsive */
.testimonials-container {
  display: flex;
  flex-direction: row;
  gap: 2rem;
  padding: 1rem;
  max-width: 100%;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  overflow-x: auto;
  padding-bottom: 1rem; /* Espace pour la scrollbar */
}

.testimonial-card {
  flex: 0 0 calc(33.333% - 1.5rem);
  min-width: 280px;
  background: white;
  border-radius: 16px;
  position: relative;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04), 0 0 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Cache la scrollbar sur certains navigateurs mais garde la fonctionnalité */
.testimonials-container::-webkit-scrollbar {
  height: 6px;
}

.testimonials-container::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.testimonials-container::-webkit-scrollbar-track {
  background-color: rgba(0, 0, 0, 0.05);
}

@media (max-width: 768px) {
  .testimonials-container {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}

.dots-navigation {
  display: none;
}

.dots-navigation {
  display: none; /* Cache les points s'ils ne sont pas nécessaires */
}

@media (max-width: 992px) {
  .testimonials-container {
    flex-direction: column;
    padding: 1rem;
    gap: 2rem;
  }
  
  .testimonial-card {
    width: 100%;
    margin-bottom: 1rem;
  }
}

.client-name {
  font-weight: bold;
  font-style: normal;
  margin-top: 1rem;
}

/* Call to Action */
.cta-container {
  text-align: center;
  margin: 3rem 0;
}

.cta-button {
  background: linear-gradient(to right, #2563EB, #7C3AED);
  color: white;
  font-size: 1.25rem;
  font-weight: bold;
  padding: 1rem 2.5rem;
  border: none;
  border-radius: 2rem;
  cursor: pointer;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.25);
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  /* Header */
  .header-content {
    padding: 4rem 1rem 2rem;
  }
  
  .main-title {
    font-size: 2rem;
  }
  
  .content-container {
    padding: 0 1rem;
  }
  
  /* Grids */
  .advantages-grid,
  .use-cases-grid {
    grid-template-columns: 1fr;
  }
  
  /* Layout */
  .text-and-illustration {
    flex-direction: column;
  }
  
  .youtube-container {
    height: 200px;
  }
  
  /* Chatbot */
  .what-is-card .text-and-illustration {
      flex-direction: column;
      align-items: center;
    }
    
    .what-is-card .text-content {
      width: 100%;
      min-width: 100%;
      order: 1;
    }
    
    .chatbot-illustration {
      width: 100%;
      min-width: 100%;
      max-width: 80%;
      margin-top: 1.5rem;
      order: 2;
    }
    
    .what-is-card .section-title {
      font-size: 1.5rem;
      text-align: center;
    }
  
  .chatbot-image {
    transform: scale(1.3);
  }
  
  /* Intro Card - Fix pour la section introduction */
  .intro-card .text-and-illustration {
      flex-direction: column;
    }
    
    .intro-card .text-content {
      width: 100%;
      min-width: 100%;
      margin-bottom: 1rem;
    }
    
    .intro-card .youtube-container {
      width: 100%;
      min-width: 100%;
      margin-top: 1rem;
    }
    
    .intro-card .section-title {
      font-size: 1.5rem;
      text-align: center;
    }
  
  /* Testimonials */
  .testimonials-container {
    padding: 1rem;
    flex-direction: column;
    gap: 2rem;
  }
  
  .testimonial-card {
    min-width: 100%;
  }
  
  .testimonials-title {
    font-size: 2rem;
    margin-bottom: 2.5rem;
  }
  
  /* Metrics */
  .metrics-banner {
    flex-direction: column;
    gap: 1.5rem;
  }
}

/* IA Enterprise Illustration */
.ia-enterprise-illustration {
  flex: 1;
  min-width: 450px;
  max-width: 500px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.ia-enterprise-image {
  max-width: 100%;
  height: auto;
}

@media (max-width: 768px) {
  .ia-enterprise-illustration {
    min-width: 100%;
    margin-top: 1.5rem;
  }
}

/* IA Enterprise Illustration */
.ia-enterprise-illustration {
  flex: 1;
  min-width: 450px;
  max-width: 500px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.ia-enterprise-image {
  max-width: 100%;
  height: auto;
}

@media (max-width: 768px) {
  .ia-enterprise-illustration {
    min-width: 100%;
    margin-top: 1.5rem;
  }
}

</style>